import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Auth } from '../../services/amplifyCompat';
import {
  Button,
  CircularProgress,
  Divider,
  Grid,
  Snackbar,
  Paper,
  useMediaQuery,
  Alert
} from '@mui/material';
import {
  ArrowBack,
  AlternateEmail,
  Lock,
  Dialpad,
  Person,
  Badge,
} from '@mui/icons-material';
import { Box } from '@mui/system';
import { makeStyles } from 'tss-react/mui';
import { updateLoginState } from '../../actions/loginActions';
import TextFieldStartAdornment from './TextFieldStartAdornment';
import './Login.css';
import PasswordRequirements from './PasswordRequirements';
import LoginNavbar from './LoginNavbar';
import useTranslation from '../customHooks/translations';
import { isSimpleSignupFlow } from '../../utils/signup-flow-type';
import { PAGE_PATHS } from '../../constants/page-paths';
import { useTheme } from '@mui/material/styles';

const initialFormState = {
  email: '',
  password: '',
  name: '',
  preferred_username: '',
  user_type: '',
  authCode: '',
  resetCode: '',
};

const useStyles = makeStyles()((theme) => {
  return {
    marginTop: {
      margin: theme.spacing(2, 'auto', 'auto', 'auto'),
    },
    marginHorizontal: {
      margin: theme.spacing(4, 'auto'),
    },
    padding: {
      padding: theme.spacing(1.5),
    },
    textAlignCenter: {
      textAlign: 'center',
    },
    flexDisplay: {
      display: 'flex',
    },
    forgetPassword: {
      justifyContent: 'flex-end',
      fontWeight: 500,
      lineHeight: 1.75,
    },
    activeButton: {
      borderRadius: 50,
      width: '100%',
      fontSize: '1em',
    },
    themeColor: {
      backgroundColor: '#0D9488',
    },
    errorMessage: {
      color: '#DC2626',
    },
    cursor: {
      cursor: 'pointer',
    },
    underlineText: {
      textDecoration: 'underline',
    },
    passwordReq: {
      backgroundColor: '#FEF2F2',
      borderRadius: 8,
    },
    centerBox: {
      justifyContent: 'center',
      alignItems: 'center',
    },
    defaultButton: {
      borderRadius: 10,
      width: '100%',
      fontSize: '1em',
      padding: theme.spacing(1.5),
      margin: theme.spacing(2, 'auto'),
      backgroundColor: 'rgba(255,255,255,0.15)',
      color: '#FFFFFF',
      border: '1px solid rgba(255,255,255,0.3)',
      '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.25)',
      },
    },
    submitButton: {
      color: '#FFFFFF',
      backgroundColor: '#0D9488',
      '&:hover': {
        backgroundColor: '#0F766E',
      },
    },
    helpText: {
      marginBottom: '20px',
      color: 'rgba(255,255,255,0.85)',
      display: 'block',
    },
    textFieldStyle: {
      marginBottom: '12px',
      '& label': {
        color: 'rgba(255,255,255,0.7)',
        fontSize: '1.1em',
        paddingRight: '5px',
        '&.Mui-focused': {
          color: '#FFFFFF',
        },
      },
      '& legend': {
        fontSize: '1em',
      },
      '& fieldset': {
        borderColor: 'rgba(255,255,255,0.25)',
        borderRadius: '10px',
      },
      '& input': {
        color: '#FFFFFF',
      },
      '& svg': {
        color: 'rgba(255,255,255,0.6)',
      },
      '& p': {
        color: 'rgba(255,255,255,0.7)',
      },
      '& .MuiOutlinedInput-root:hover fieldset': {
        borderColor: 'rgba(255,255,255,0.5)',
      },
      '& .MuiOutlinedInput-root.Mui-focused fieldset': {
        borderColor: '#5EEAD4',
      },
    },
  };
});

/* Login component options:
 *
 * [logo: "custom", "none"]
 * [type: "video", "image", "static"]
 * [themeColor: "standard", "#012144" (color hex value in quotes) ]
 *  Suggested alternative theme colors: #037dad, #5f8696, #495c4e, #4f2828, #ba8106, #965f94
 * [animateTitle: true, false]
 * [title: string]
 * [darkMode (changes font/logo color): true, false]
 * [disableSignUp: true, false]
 * */

function Login(props) {
  const {
    loginState,
    updateLoginState,
    animateTitle,
    type,
    title,
    darkMode,
    themeColor,
    disableSignUp,
  } = props;

  const theme = useTheme();
  const [formState, updateFormState] = useState(initialFormState);
  const [accountCreationEmailExistError, setAccountCreationEmailExistError] =
    useState(false);
  const [accountCreationPasswordError, setAccountCreationPasswordError] =
    useState(false);
  const [accountLoginError, setAccountLoginError] = useState(false);
  const [verificationError, setVerificationError] = useState(false);
  const [newPasswordError, setNewPasswordError] = useState(false);
  const [newVerification, setNewVerification] = useState(false);
  const [loading, setLoading] = useState(false);
  const [gLoading, setGLoading] = useState(false);
  const [aLoading, setALoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [forgotPasswordError, setForgotPasswordError] = useState(false);
  const [emptyInputError, setEmptyInputError] = useState(false);
  const [invalidEmailError, setInvalidEmailError] = useState(false);
  const [timeLimitError, setTimeLimitError] = useState('');
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  const translation = useTranslation();

  // password check
  const [passwordRequirements, setPasswordRequirements] = useState({
    uppercase: { error: false, description: 'oneUppercase' },
    lowercase: { error: false, description: 'oneLowercase' },
    digit: { error: false, description: 'oneDigit' },
    special: { error: false, description: 'oneSpecialCharacter' },
    minLength: {
      error: false,
      description: 'more8Characters',
    },
    maxLength: {
      error: false,
      description: 'less16Characters',
    },
  });
  const [passwordUnmatchError, setPasswordUnmatchError] = useState(false);
  const [confirmPasswordString, setConfirmPasswordString] = useState('');

  const { classes } = useStyles();

  useEffect(() => {
    async function retrieveUser() {
      try {
        await Auth.currentAuthenticatedUser();
        updateLoginState('signedIn');
      } catch {
        updateLoginState('signIn');
      }
    }
    retrieveUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function clearErrors() {
    setAccountCreationEmailExistError(false);
    setAccountCreationPasswordError(false);
    setAccountLoginError(false);
    setVerificationError(false);
    setNewVerification(false);
    setNewPasswordError(false);
    setInvalidEmailError(false);
    setForgotPasswordError(false);
    setEmptyInputError(false);
    setPasswordUnmatchError(false);
    setTimeLimitError('');
  }

  function onChange(e) {
    e.persist();
    clearErrors();

    updateFormState({ ...formState, [e.target.name]: e.target.value });
  }

  function onChangePassword(e) {
    const currPW = e.target.value;
    setPasswordRequirements(() => {
      passwordRequirements.uppercase.error = /[A-Z]/.test(currPW);
      passwordRequirements.lowercase.error = /[a-z]/.test(currPW);
      passwordRequirements.digit.error = /[0-9]/.test(currPW);
      passwordRequirements.special.error = /[^A-Za-z0-9]/.test(currPW);
      passwordRequirements.minLength.error = currPW.length >= 8;
      passwordRequirements.maxLength.error = currPW.length <= 16;

      return passwordRequirements;
    });

    onChange(e);

    // Only show mismatch once the confirm field has been typed
    if (confirmPasswordString) {
      setPasswordUnmatchError(e.target.value !== confirmPasswordString);
    } else {
      setPasswordUnmatchError(false);
    }
  }

  async function signUp() {
    try {
      // check if both passwords match first before signing up
      checkMatchingPasswords();

      const { email, password, name, preferred_username } = formState;
      const normalizedEmail = email?.trim();
      checkEmptyString(name);
      checkEmptyString(preferred_username);

      setLoading(true);
      await Auth.signUp({
        username: normalizedEmail,
        password: password,
        attributes: {
          name: name,
          preferred_username: preferred_username,
          'custom:type': 'User',
          'custom:firstLogin': 'true',
        },
      });

      updateFormState(() => ({
        ...initialFormState,
        name,
        preferred_username,
        email: normalizedEmail,
      }));
      updateLoginState('confirmSignUp');
      setLoading(false);
    } catch (e) {
      setLoading(false);
      setEmptyInputError(false);
      const errorMsg = e.message;

      if (errorMsg.includes('empty')) {
        setEmptyInputError(true);
      } else if (errorMsg.includes('Username should be an email.')) {
        setInvalidEmailError(true);
      } else if (errorMsg.includes('given email already exists')) {
        setAccountCreationEmailExistError(true);
      } else if (errorMsg.includes('Passwords do not match')) {
        setPasswordUnmatchError(true);
      } else {
        setAccountCreationPasswordError(true);
      }
    }
  }

  // confirmSignUp shows after signUp page
  async function confirmSignUp() {
    // Verify Account with confirmation code after sign up page
    try {
      setNewVerification(false);
      const { email, authCode } = formState;
      setLoading(true);
      await Auth.confirmSignUp(email?.trim(), authCode?.trim());
      resetStates('signIn');
      setShowSuccessAlert(true);
      setLoading(false);
    } catch (e) {
      console.log(e);
      setVerificationError(true);
      setLoading(false);

      const errorMsg = e.message;
      if (errorMsg.includes('time')) {
        setTimeLimitError(errorMsg);
      }
    }
  }

  async function resendConfirmationCode() {
    try {
      const { email } = formState;
      setVerificationError(false);
      await Auth.resendSignUp(email?.trim());
      setNewVerification(true);
    } catch (err) {
      setNewVerification(false);

      const errorMsg = err.message;
      if (errorMsg.includes('time')) {
        setTimeLimitError(errorMsg);
      }
    }
  }

  async function completeGoogleSignin() {
    setGLoading(false);
  }

  async function cancelSignin() {
    setGLoading(false);
    setALoading(false);
  }

  async function signInGoogle() {
    try {
      setGLoading(true);
      if (window.isNative) {
        window.cancelSignin = cancelSignin;
        window.completeGoogleSignin = completeGoogleSignin;
        window.ReactNativeWebView.postMessage(`SocialSignIn-google`)
      } else {
        await Auth.federatedSignIn({ provider: 'Google' });
        setGLoading(false);
      }
    } catch {
      setGLoading(false);
    }
  }

  function completeAppleSignin() {
    setALoading(false);
  }

  async function signInApple() {
    try {
      setALoading(true);
      if (window.isNative) {
        window.cancelSignin = cancelSignin;
        window.completeAppleSignin = completeAppleSignin;
        window.ReactNativeWebView.postMessage(`SocialSignIn-apple`)
      } else {
        await Auth.federatedSignIn({ provider: 'SignInWithApple' });
        setALoading(false);
      }
    } catch {
      setALoading(false);
    }
  }

  async function signIn() {
    try {
      setLoading(true);
      const { email, password } = formState;
      const normalizedEmail = email?.trim();
      let user = await Auth.signIn(normalizedEmail, password);
      if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
        // a new password needs to be set if account is created through Amazon Cognito for the user
        resetStates('newUserPassword');
        setLoading(false);
        setCurrentUser(user);
      } else {
        resetStates('signedIn');
        setLoading(false);
      }
    } catch (e) {
      setLoading(false);

      const isPasswordResetRequired =
        e?.code === 'PasswordResetRequiredException' ||
        e?.name === 'PasswordResetRequiredException' ||
        e?.message?.includes('PasswordResetRequiredException');

      const isUserNotConfirmed =
        e?.code === 'UserNotConfirmedException' ||
        e?.name === 'UserNotConfirmedException' ||
        e?.message?.includes('UserNotConfirmedException') ||
        e?.message?.includes('User is not confirmed');

      // if a password is requested through Amazon Cognito,
      // need to jump to resetPassword page
      if (isPasswordResetRequired) {
        const { email } = formState;

        updateFormState(() => ({ ...initialFormState, email: email?.trim() }));
        updateLoginState('resetPassword');
        setLoading(false);
      } else if (isUserNotConfirmed) {
        // Unconfirmed accounts need to verify email before signing in
        const { email } = formState;
        const normalizedEmail = email?.trim();
        updateFormState(() => ({ ...initialFormState, email: normalizedEmail }));
        updateLoginState('confirmSignUp');
        try {
          await Auth.resendSignUp(normalizedEmail);
          setNewVerification(true);
        } catch (resendErr) {
          const errorMsg = resendErr?.message || '';
          if (errorMsg.includes('time')) {
            setTimeLimitError(errorMsg);
          }
        }
      } else {
        setAccountLoginError(true);
      }
    }
  }

  async function setNewPassword() {
    try {
      // check if both passwords match first before setting new password
      checkMatchingPasswords();

      const { password } = formState;
      setLoading(true);
      await Auth.completeNewPassword(currentUser, password);
      resetStates('signedIn');
      setLoading(false);
    } catch (e) {
      setLoading(false);

      const errorMsg = e.message;

      if (errorMsg.includes('empty')) {
        setEmptyInputError(true);
      } else if (errorMsg.includes('Passwords do not match')) {
        setPasswordUnmatchError(true);
      } else if (errorMsg.includes('time')) {
        setTimeLimitError(errorMsg);
      } else {
        setNewPasswordError(true);
      }
    }
  }

  async function forgotPassword() {
    try {
      const { email } = formState;
      const normalizedEmail = email?.trim();
      setLoading(true);
      await Auth.forgotPassword(normalizedEmail);
      updateFormState(() => ({ ...initialFormState, email: normalizedEmail }));
      updateLoginState('resetPassword');
      setLoading(false);
    } catch {
      setLoading(false);
      setForgotPasswordError(true);
    }
  }

  // resetPassword after forgotPassword page
  async function resetPassword() {
    try {
      // check if both passwords match first before resetting password
      checkMatchingPasswords();

      const { email, resetCode, password } = formState;
      setLoading(true);
      await Auth.forgotPasswordSubmit(email?.trim(), resetCode?.trim(), password);
      resetStates('signIn');
      setLoading(false);
    } catch (e) {
      setLoading(false);
      setEmptyInputError(false);

      const errorMsg = e.message;

      if (errorMsg.includes('empty')) {
        setEmptyInputError(true);
      } else if (errorMsg.includes('verification code')) {
        setVerificationError(true);
      } else if (errorMsg.includes('time')) {
        setTimeLimitError(errorMsg);
      } else if (errorMsg.includes('Passwords do not match')) {
        setPasswordUnmatchError(true);
      } else {
        setNewPasswordError(true);
      }
    }
  }

  function checkMatchingPasswords() {
    // check if both passwords match
    if (!confirmPasswordString) {
      // empty field
      throw new Error('empty');
    } else if (passwordUnmatchError) {
      throw new Error('Passwords do not match');
    }
  }

  function checkEmptyString(str) {
    // check if string is empty after space trimmed
    if (str.replace(/\s+/g, '') === '') {
      throw new Error('empty');
    }
  }

  function resetStates(state) {
    // clear states when hitting the back button
    updateFormState((p) => ({ ...initialFormState, email: p.email }));
    clearErrors();
    setConfirmPasswordString('');

    // clear password requirement checks
    setPasswordRequirements(() => {
      passwordRequirements.uppercase.error = false;
      passwordRequirements.lowercase.error = false;
      passwordRequirements.digit.error = false;
      passwordRequirements.special.error = false;
      passwordRequirements.minLength.error = false;
      passwordRequirements.maxLength.error = false;

      return passwordRequirements;
    });

    updateLoginState(state);
  }

  // set email if redirected from landing page
  useEffect(() => {
    const url = new URL(window.location.href);
    const email = url.searchParams.get('email');
    if (!email) {
      return;
    }
    updateFormState((p) => ({ ...p, email }));
  }, []);

  const isBelowMedium = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <>
      {/*  An example image is provided. Please use a royalty-free photo, a photo owned by you, or a photo owned by the CIC */}
      <Grid
        container
        className={classes.centerBox}
        style={
          type === 'image'
            ? themeColor === 'standard'
              ? {
                background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #0F172A 100%)',
                backgroundSize: 'cover',
                width: '100%',
                minHeight: '100vh',
                position: 'relative',
              }
              : {
                background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #0F172A 100%)',
                backgroundSize: 'cover',
                width: '100%',
                height: isBelowMedium ? '115vh' : '100vh',
              }
            : themeColor === 'standard'
              ? { background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)', width: '100%', height: '100vh' }
              : { background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)', width: '100%', height: '100vh' }
        }
      >
        <LoginNavbar />

        {/* Please use a royalty free video or a video that you or the CIC owns */}
        {type === 'video' ? (
          <video playsInline autoPlay muted loop>
            <source
              src="/Assets/Videos/video.mp4"
              type="video/mp4"
            />
          </video>
        ) : null}
        <Grid
          container
          item
          xs={12}
          md={6}
          sx={{ display: { xs: 'none', md: 'block' } }}
          className={`page-info ${classes.centerBox}`}
        >
          <Grid
            container
            item
            justifyContent={'space-evenly'}
            alignItems={'center'} /*style={{height: "60vh"}}*/
          >
            <Grid xs item className={`typewriter ${classes.marginHorizontal}`}>
              <p
                className={`${classes.textAlignCenter} ${animateTitle
                  ? darkMode
                    ? 'line anim-typewriter'
                    : 'line anim-typewriter-light lightMode'
                  : darkMode
                    ? 'line-static'
                    : 'line-static lightMode-static'
                  }`}
              >
                {title}
              </p>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          container
          item
          xs={12}
          sm={7}
          md={5}
          className={`login-container ${classes.centerBox}`}
          sx={{
            marginTop: '4em',
            pl: { md: 6, lg: 10 },
          }}
        >
          <Paper variant="gradient">
            <Grid
              container
              item
              direction={'column'}
              wrap={'nowrap'}
              xs={12}
              sm={11}
              md={9}
              className={'login-box'}
            >
              <Grid className={'login-wrapper-top'}>
                <span className={'login-wrapper-top-header'}>
                  {loginState === 'signIn' ? (
                    <span>{translation.signIn}</span>
                  ) : loginState === 'signUp' ? (
                    <span>{translation.createAccount}</span>
                  ) : loginState === 'confirmSignUp' ? (
                    <span>{translation.verifyAccount}</span>
                  ) : loginState === 'forgotPassword' ? (
                    <span>{translation.forgotPassword}</span>
                  ) : loginState === 'resetPassword' ? (
                    <span>{translation.passwordReset}</span>
                  ) : loginState === 'newUserPassword' ? (
                    <span>{translation.setNewPassword}</span>
                  ) : (
                    <span>{translation.welcome}</span>
                  )}
                </span>
              </Grid>
              {loginState === 'signIn' && (
                <Grid>
                  <Snackbar
                    autoHideDuration={2000}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    open={showSuccessAlert}
                    message="Success! Redirecting you to sign in page"
                    onClose={() => setShowSuccessAlert(false)}
                  />
                  <BannerMessage type={'error'} typeCheck={accountLoginError}>
                    {translation.incorrectLogin}
                  </BannerMessage>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (!loading) {
                        signIn();
                      }
                    }}
                  >
                    {/* username */}
                    <TextFieldStartAdornment
                      startIcon={<AlternateEmail />}
                      name={'email'}
                      type={'email'}
                      onChange={onChange}
                      value={formState.email}
                      label={translation.email}
                      className={classes.textFieldStyle}
                    />
                    {/* password */}
                    <TextFieldStartAdornment
                      startIcon={<Lock />}
                      name={'password'}
                      type={'password'}
                      onChange={onChange}
                      value={formState.password}
                      label={translation.password}
                      className={classes.textFieldStyle}
                    />
                    <Grid
                      className={`${classes.flexDisplay} ${classes.forgetPassword} ${classes.cursor}`}
                      onClick={() => resetStates('forgotPassword')}
                    >
                      {' '}
                      {/* forget */}
                      <span style={{ textAlign: 'end' }}>
                        {translation.forgotPassword}
                      </span>
                    </Grid>
                    <Grid className={`input-box`} style={{ marginTop: '0px' }}>
                      {' '}
                      {/* sign in button */}
                      <SubmitButtonWithLoading
                        type="submit"
                        submitAction={signIn}
                        submitMessage={translation.signIn}
                        loadingState={loading}
                      />
                    </Grid>
                  </form>
                  <Grid className={`input-box`} style={{ marginTop: '0px' }}>
                    {' '}
                    {/* sign in button */}
                    <GoogleSubmitButtonWithLoading
                      submitAction={signInGoogle}
                      submitMessage={translation.signInGoogle}
                      loadingState={gLoading}
                    />
                  </Grid>
                  <Grid className={`input-box`} style={{ marginTop: '0px' }}>
                    {' '}
                    {/* sign in button */}
                    <AppleSubmitButtonWithLoading
                      submitAction={signInApple}
                      submitMessage='Sign in with Apple'
                      loadingState={aLoading}
                    />
                  </Grid>
                  {/* sign in button */}
                  {/* <FacebookSubmitButtonWithLoading
                      submitAction={signInFacebook}
                      submitMessage={translation.signInFacebook}
                      loadingState={fbLoading}
                    /> */}
                  {/* </Grid> */}
                  {!disableSignUp && ( // if sign up is not disabled, then show the create an account option
                    <div>
                      {/* divider */}
                      <Grid container item alignItems="center" xs={12}>
                        <Grid item xs>
                          <Divider />
                        </Grid>
                        <Grid item className={classes.padding}>
                          {translation.or}
                        </Grid>
                        <Grid item xs>
                          <Divider />
                        </Grid>
                      </Grid>
                      {/* create an account button */}
                      <Grid className={`input-box`}>
                        {isSimpleSignupFlow() ? (
                          <Button
                            variant="contained"
                            type="button"
                            onClick={() => resetStates('signUp')}
                            className={classes.defaultButton}
                          >
                            {translation.createAccount}
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            component={Link}
                            className={classes.defaultButton}
                            to={PAGE_PATHS.SIGNUP}
                          >
                            {translation.createAccount}
                          </Button>
                        )}
                      </Grid>
                    </div>
                  )}
                </Grid>
              )}
              {loginState === 'forgotPassword' && (
                <Grid>
                  <Grid container item xs={12}>
                    <span className={classes.helpText}>
                      {translation.enterEmailAddress}
                    </span>
                  </Grid>
                  <TextFieldStartAdornment
                    startIcon={<AlternateEmail />}
                    label={translation.email}
                    name={'email'}
                    type="email"
                    autoComplete={'new-password'}
                    variant="outlined"
                    error={forgotPasswordError}
                    onChange={onChange}
                    value={formState.email}
                    className={classes.textFieldStyle}
                  />
                  {!!forgotPasswordError && (
                    <Grid container item xs={12} className={classes.errorMessage}>
                      <span>
                        {translation.validEmailAccount}&nbsp;
                        <span
                          className={`${classes.cursor} ${classes.underlineText}`}
                          onClick={() => updateLoginState('signUp')}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              updateLoginState('signUp');
                            }
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <strong>{translation.here}</strong>
                        </span>
                        <span>.</span>
                      </span>
                    </Grid>
                  )}
                  <BackAndSubmitButtons
                    backAction={() => resetStates('signIn')}
                    submitAction={forgotPassword}
                    submitMessage={translation.sendResetCode}
                    loadingState={loading}
                  />
                </Grid>
              )}
              {loginState === 'resetPassword' && (
                <Grid>
                  <Grid>
                    <span className={classes.helpText}>
                      {translation.checkEmail}&nbsp;
                      <strong>{formState.email}</strong>
                      <br />
                      {translation.forResetCodeAndCreatePassword}
                    </span>
                  </Grid>
                  <BannerMessage
                    type={'error'}
                    typeCheck={emptyInputError || timeLimitError}
                  >
                    {(!!emptyInputError && translation.fillAllFields) ||
                      (timeLimitError !== '' && timeLimitError)}
                  </BannerMessage>
                  <TextFieldStartAdornment
                    startIcon={<Dialpad />}
                    label={translation.enterResetCode}
                    variant="outlined"
                    name={'resetCode'}
                    type="text"
                    error={verificationError}
                    className={classes.textFieldStyle}
                    helperText={
                      !!verificationError && translation.enterCorrectResetCode
                    }
                    onChange={onChange}
                    value={formState.resetCode}
                  />
                  <TextFieldStartAdornment
                    startIcon={<Lock />}
                    label={translation.createNewPassword}
                    name={'password'}
                    type="password"
                    error={newPasswordError}
                    helperText={translation.passwordSettings}
                    autoComplete={'new-password'}
                    onChange={onChangePassword}
                    value={formState.password}
                    className={classes.textFieldStyle}
                  />
                  <Grid
                    container
                    item
                    xs={12}
                    className={newPasswordError ? classes.passwordReq : null}
                  >
                    <PasswordRequirements requirements={passwordRequirements} />
                  </Grid>
                  <TextFieldStartAdornment
                    startIcon={<Lock />}
                    //   label="Password"
                    label={translation.reEnterPassword}
                    name={'confirm-password'}
                    type="password"
                    error={passwordUnmatchError}
                    helperText={
                      !!passwordUnmatchError && translation.passwordNotMatch
                    }
                    autoComplete={'new-password'}
                    className={classes.textFieldStyle}
                    value={confirmPasswordString}
                    onChange={(e) => {
                      setConfirmPasswordString(e.target.value); // update current input state
                      // check if "password" is the same as "confirm-password"
                      e.target.value === formState.password
                        ? setPasswordUnmatchError(false)
                        : setPasswordUnmatchError(true);
                    }}
                  />
                  <BackAndSubmitButtons
                    backAction={() => resetStates('signIn')}
                    submitAction={resetPassword}
                    submitMessage={translation.updatePassword}
                    loadingState={loading}
                  />
                </Grid>
              )}
              {loginState === 'signUp' && (
                <Grid>
                  <BannerMessage type={'error'} typeCheck={emptyInputError}>
                    {translation.fillAllFields}
                  </BannerMessage>
                  <TextFieldStartAdornment
                    startIcon={<Badge />}
                    label={translation.name}
                    name={'name'}
                    type="text"
                    autoComplete={'new-password'}
                    onChange={onChange}
                    value={formState.name}
                    className={classes.textFieldStyle}
                  />
                  <TextFieldStartAdornment
                    startIcon={<Person />}
                    label={translation.username}
                    name={'preferred_username'}
                    type="text"
                    autoComplete={'new-password'}
                    onChange={onChange}
                    className={classes.textFieldStyle}
                  />
                  <TextFieldStartAdornment
                    className={classes.textFieldStyle}
                    startIcon={<AlternateEmail />}
                    label={translation.email}
                    name={'email'}
                    type="email"
                    autoComplete={'new-password'}
                    error={accountCreationEmailExistError || invalidEmailError}
                    helperText={
                      (!!accountCreationEmailExistError &&
                        translation.emailAlreadyExists) ||
                      (!!invalidEmailError && translation.validEmail)
                    }
                    onChange={onChange}
                    value={formState.email}
                  />
                  <TextFieldStartAdornment
                    startIcon={<Lock />}
                    label={translation.password}
                    name={'password'}
                    type="password"
                    error={accountCreationPasswordError}
                    helperText={translation.passwordSettings}
                    autoComplete={'new-password'}
                    onChange={onChangePassword}
                    value={formState.password}
                    className={classes.textFieldStyle}
                  />
                  <Grid
                    container
                    item
                    xs={12}
                    className={
                      accountCreationPasswordError ? classes.passwordReq : null
                    }
                  >
                    <PasswordRequirements requirements={passwordRequirements} />
                  </Grid>
                  <TextFieldStartAdornment
                    startIcon={<Lock />}
                    label={translation.confirmPassword}
                    name={'confirm-password'}
                    type="password"
                    error={passwordUnmatchError}
                    helperText={
                      !!passwordUnmatchError && translation.passwordNotMatch
                    }
                    autoComplete={'new-password'}
                    value={confirmPasswordString}
                    className={classes.textFieldStyle}
                    onChange={(e) => {
                      setConfirmPasswordString(e.target.value); // update current input state
                      // check if "password" is the same as "confirm-password"
                      e.target.value === formState.password
                        ? setPasswordUnmatchError(false)
                        : setPasswordUnmatchError(true);
                    }}
                  />
                  <BackAndSubmitButtons
                    backAction={() => resetStates('signIn')}
                    submitAction={signUp}
                    submitMessage={translation.signUp}
                    loadingState={loading}
                  />
                </Grid>
              )}
              {loginState === 'confirmSignUp' && (
                <Grid>
                  <Grid container item xs={12}>
                    <span>{translation.checkEmailForConfirmationCode}</span>
                  </Grid>
                  <BannerMessage type={'error'} typeCheck={verificationError}>
                    {translation.invalidVerificationCode}
                  </BannerMessage>
                  <BannerMessage type={'error'} typeCheck={timeLimitError !== ''}>
                    {timeLimitError}
                  </BannerMessage>
                  <BannerMessage type={'success'} typeCheck={newVerification}>
                    {translation.newVerificationCodeSentSuccesfully}
                  </BannerMessage>
                  <Grid
                    container
                    item
                    direction={'column'}
                    xs={12}
                    className={'input-box'}
                  >
                    <TextFieldStartAdornment
                      startIcon={<Dialpad />}
                      label={translation.enterConfirmationCode}
                      name={'authCode'}
                      type="text"
                      autoComplete={'new-password'}
                      onChange={onChange}
                      value={formState.authCode}
                      className={classes.textFieldStyle}
                    />
                  </Grid>
                  <Grid>
                    <span>{translation.didNotReceiveCode}</span>
                    <Button onClick={resendConfirmationCode}>
                      <span className={classes.underlineText}>
                        {translation.resendCode}
                      </span>
                    </Button>
                  </Grid>
                  <BackAndSubmitButtons
                    backAction={() => resetStates('signUp')}
                    submitAction={confirmSignUp}
                    submitMessage={'Verify'}
                    loadingState={loading}
                  />
                </Grid>
              )}
              {loginState === 'newUserPassword' && (
                <Grid>
                  <Grid container item xs={12}>
                    <span>
                      {translation.replaceTemporaryPassword}{' '}
                      <strong>{formState.email}</strong>.
                    </span>
                  </Grid>
                  <BannerMessage type={'error'} typeCheck={timeLimitError !== ''}>
                    {timeLimitError}
                  </BannerMessage>
                  <Grid className={`input-box`}>
                    <TextFieldStartAdornment
                      startIcon={false}
                      // label={'Enter new password'}
                      label={translation.password}
                      name={'password'}
                      type="password"
                      autoComplete={'new-password'}
                      error={newPasswordError || emptyInputError}
                      helperText={translation.passwordSettings}
                      onChange={onChangePassword}
                      value={formState.password}
                      className={classes.textFieldStyle}
                    />
                    <Grid
                      container
                      item
                      xs={12}
                      className={
                        !!newPasswordError || !!emptyInputError
                          ? classes.passwordReq
                          : null
                      }
                    >
                      <PasswordRequirements requirements={passwordRequirements} />
                    </Grid>
                    <TextFieldStartAdornment
                      startIcon={false}
                      // label={'Re-enter new password'}
                      label={translation.confirmPassword}
                      name={'confirm-password'}
                      type="password"
                      error={passwordUnmatchError}
                      className={classes.textFieldStyle}
                      helperText={
                        !!passwordUnmatchError && translation.passwordNotMatch
                      }
                      autoComplete={'new-password'}
                      value={confirmPasswordString}
                      onChange={(e) => {
                        setConfirmPasswordString(e.target.value); // update current input state
                        // check if "password" is the same as "confirm-password"
                        e.target.value === formState.password
                          ? setPasswordUnmatchError(false)
                          : setPasswordUnmatchError(true);
                      }}
                    />
                  </Grid>
                  <BackAndSubmitButtons
                    backAction={() => resetStates('signIn')}
                    submitAction={setNewPassword}
                    submitMessage={translation.setPassword}
                    loadingState={loading}
                  />
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </>
  );
}

/* helper components */

const BannerMessage = (props) => {
  const { type, typeCheck, children } = props;

  const useStyles = makeStyles()((theme) => {
    return {
      root: {
        width: '100%',
        margin: theme.spacing(2, 'auto'),
      },
    };
  });

  const { classes } = useStyles();

  return (
    <Grid>
      {!!typeCheck && (
        <Grid container item xs={12}>
          <Alert
            className={classes.root}
            variant="filled"
            severity={type}
            elevation={3}
          >
            {children}
          </Alert>
        </Grid>
      )}
    </Grid>
  );
};

const GoogleSubmitButtonWithLoading = (props) => {
  const useStyles = makeStyles()((theme) => {
    return {
      progress: {
        display: 'flex',
        padding: theme.spacing(0, 1),
      },
      submitButton: {
        borderRadius: 10,
        width: '100%',
        fontSize: '1em',
        padding: theme.spacing(1.5),
        color: '#0F172A',
        backgroundColor: '#FFFFFF',
        border: 'none',
        fontWeight: 600,
        '&:hover': {
          backgroundColor: '#F8FAFC',
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        },
      },
    };
  });

  const { submitAction, submitMessage, loadingState } = props;
  const { classes } = useStyles();

  return (
    <Button
      variant="contained"
      disabled={!!loadingState}
      onClick={submitAction}
      className={classes.submitButton}
      style={props.style}
    >
      <Box
        component="img"
        sx={{
          marginRight: '15px',
          fontSize: 30,
          color: '#0F172A',
        }}
        alt=""
        src="./assets/images/google-icon.svg"
      />
      {submitMessage}
      {/* if it is loading, show the loading indicator */}
      {!!loadingState && (
        <Grid className={classes.progress}>
          <CircularProgress size={15} />
        </Grid>
      )}
    </Button>
  );
};

const AppleSubmitButtonWithLoading = (props) => {
  const useStyles = makeStyles()((theme) => {
    return {
      progress: {
        display: 'flex',
        padding: theme.spacing(0, 1),
      },
      submitButton: {
        borderRadius: 10,
        width: '100%',
        fontSize: '1em',
        padding: theme.spacing(1.5),
        color: '#0F172A',
        backgroundColor: '#FFFFFF',
        border: 'none',
        fontWeight: 600,
        '&:hover': {
          backgroundColor: '#F8FAFC',
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        },
      },
    };
  });

  const { submitAction, submitMessage, loadingState } = props;
  const { classes } = useStyles();

  return (
    <Button
      variant="contained"
      disabled={!!loadingState}
      onClick={submitAction}
      className={classes.submitButton}
      style={props.style}
    >
      <Box
        component="img"
        sx={{
          marginRight: '15px',
          fontSize: 30,
          color: '#0F172A',
        }}
        alt=""
        src="./assets/images/apple-icon.svg"
      />
      {submitMessage}
      {/* if it is loading, show the loading indicator */}
      {!!loadingState && (
        <Grid className={classes.progress}>
          <CircularProgress size={15} />
        </Grid>
      )}
    </Button>
  );
};

const SubmitButtonWithLoading = (props) => {
  const useStyles = makeStyles()((theme) => {
    return {
      progress: {
        display: 'flex',
        padding: theme.spacing(0, 1),
      },
      submitButton: {
        borderRadius: 10,
        width: '100%',
        fontSize: '1em',
        fontWeight: 600,
        padding: theme.spacing(1.5),
        margin: theme.spacing(2, 'auto'),
        color: '#FFFFFF',
        backgroundColor: '#0F172A',
        border: '1px solid rgba(255,255,255,0.55)',
        boxShadow: '0 8px 18px rgba(2,6,23,0.35)',
        '&:hover': {
          backgroundColor: '#020617',
          transform: 'translateY(-1px)',
          boxShadow: '0 12px 24px rgba(2,6,23,0.4)',
        },
      },
    };
  });

  const { submitAction, submitMessage, loadingState, type = 'button' } = props;
  const { classes } = useStyles();

  return (
    <Button
      type={type}
      variant="contained"
      disabled={!!loadingState}
      // When type="submit", the parent form onSubmit handles the action
      onClick={type === 'submit' ? undefined : submitAction}
      className={classes.submitButton}
      style={props.style}
    >
      {submitMessage}
      {/* if it is loading, show the loading indicator */}
      {!!loadingState && (
        <Grid className={classes.progress}>
          <CircularProgress size={15} />
        </Grid>
      )}
    </Button>
  );
};

const BackAndSubmitButtons = ({ backAction, ...others }) => {
  const { classes } = useStyles();

  const translation = useTranslation();

  return (
    <Grid container item xs={12} justifyContent="space-between" spacing={1}>
      <Grid container item xs>
        <Button
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={backAction}
          className={classes.defaultButton}
        >
          {translation.back}
        </Button>
      </Grid>
      <Grid container item md={7} justifyContent={'flex-end'}>
        <SubmitButtonWithLoading {...others} />
      </Grid>
    </Grid>
  );
};

const mapStateToProps = (state) => {
  return {
    loginState: state.loginState.currentState,
  };
};

const mapDispatchToProps = {
  updateLoginState,
};

export default connect(mapStateToProps, mapDispatchToProps)(Login);
